import './App.css';
import { useState, useEffect, useRef } from 'react';
import GuessBoxes from './components/GuessBoxes';
import QuoteGuessBoxes from './components/QuoteGuessBoxes';
import ConversationGuessBoxes from './components/ConversationGuessBoxes';
import Modal from './components/Modal';
import AutocompleteDropdown from './components/AutocompleteDropdown';
import GameModeSelector from './components/GameModeSelector';
import * as people from './data/people.js';
import { quotes } from './data/quotes.js';
import { conversations } from './data/convos.js';
import { generateShareText, copyToClipboard } from './utils/shareUtils';

// Helper function to generate hash from string
function generateHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Helper function to get date string in YYYY-MM-DD format
function getDateString(date) {
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  );
}

// Function to get the person of the day based on current date
function getPersonOfTheDay() {
  const allPeople = Object.values(people);
  const today = new Date();

  // Build up the complete history for the last 5 days
  const history = {};

  // Calculate persons for the last 5 days first
  for (let i = 5; i >= 1; i--) {
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - i);
    const pastDateString = getDateString(pastDate);

    // Get recent people for this past date (only look at days before it)
    const recentForPastDate = [];
    for (let j = i + 1; j <= 5; j++) {
      const evenEarlierDate = new Date(today);
      evenEarlierDate.setDate(today.getDate() - j);
      const evenEarlierDateString = getDateString(evenEarlierDate);
      if (history[evenEarlierDateString]) {
        recentForPastDate.push(history[evenEarlierDateString]);
      }
    }

    // Find person for this past date
    let hash = generateHash(pastDateString);
    let index = hash % allPeople.length;
    let selectedPerson = allPeople[index];

    let attempt = 0;
    while (
      recentForPastDate.includes(selectedPerson.name) &&
      attempt < allPeople.length
    ) {
      const backupSeed = pastDateString + '_backup_' + attempt;
      hash = generateHash(backupSeed);
      index = hash % allPeople.length;
      selectedPerson = allPeople[index];
      attempt++;
    }

    history[pastDateString] = selectedPerson.name;
  }

  // Now calculate today's person
  const todayString = getDateString(today);
  const recentPeople = Object.values(history);

  let hash = generateHash(todayString);
  let index = hash % allPeople.length;
  let selectedPerson = allPeople[index];

  let attempt = 0;
  while (
    recentPeople.includes(selectedPerson.name) &&
    attempt < allPeople.length
  ) {
    const backupSeed = todayString + '_backup_' + attempt;
    hash = generateHash(backupSeed);
    index = hash % allPeople.length;
    selectedPerson = allPeople[index];
    attempt++;
  }

  return selectedPerson;
}

// Function to get the quote of the day based on current date
function getQuoteOfTheDay() {
  // Filter out quotes with null authors
  const validQuotes = quotes.filter((quote) => quote.author !== null);

  const today = new Date();
  const todayString = getDateString(today);
  const hash = generateHash(todayString + '_quote_daily_selection_');
  const index = hash % validQuotes.length;
  return validQuotes[index];
}

// Function to get the conversation of the day based on current date
function getConversationOfTheDay() {
  const today = new Date();
  const todayString = getDateString(today);
  const hash = generateHash(
    todayString +
      '_conversation_daily_selection_csqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq',
  );
  const index = hash % conversations.length;
  return conversations[index];
}

// Function to format the current date for display
function getCurrentDateString() {
  const today = new Date();
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return today.toLocaleDateString('en-US', options);
}

// Function to format a date string with weekday
function getFormattedDateWithWeekday(dateString) {
  const date = new Date(dateString);
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
}

function App() {
  const [gameMode, setGameMode] = useState(null); // 'person', 'quote', or 'conversation'
  const guessLimit = 5;
  const [guesses, setGuesses] = useState([]);
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'lost'
  const [showModal, setShowModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputValue2, setInputValue2] = useState(''); // Second input for conversation mode
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showAutocomplete2, setShowAutocomplete2] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedIndex2, setSelectedIndex2] = useState(-1);
  const inputRef = useRef(null);
  const inputRef2 = useRef(null);

  // Initialize guesses array based on game mode
  useEffect(() => {
    if (gameMode === 'person') {
      setGuesses(Array(guessLimit).fill(null));
    } else if (gameMode === 'quote' || gameMode === 'conversation') {
      setGuesses([]);
    }
  }, [gameMode, guessLimit]);

  const correctAnswer = gameMode === 'person' ? getPersonOfTheDay() : null;
  const correctQuote = gameMode === 'quote' ? getQuoteOfTheDay() : null;
  const correctConversation =
    gameMode === 'conversation' ? getConversationOfTheDay() : null;

  // Check for win/lose conditions
  useEffect(() => {
    if (gameMode === 'person') {
      const validGuesses = guesses.filter((g) => g !== null);
      const hasWon = validGuesses.some(
        (guess) => guess.toLowerCase() === correctAnswer.name.toLowerCase(),
      );
      const hasLost = validGuesses.length === guessLimit && !hasWon;

      if (hasWon && gameState === 'playing') {
        setGameState('won');
        setTimeout(() => setShowModal(true), 500);
      } else if (hasLost && gameState === 'playing') {
        setGameState('lost');
        setTimeout(() => setShowModal(true), 500);
      }
    } else if (gameMode === 'quote') {
      const validGuesses = guesses.filter((g) => g !== null);
      const hasWon = validGuesses.some(
        (guess) => guess.toLowerCase() === correctQuote?.author?.toLowerCase(),
      );

      if (hasWon && gameState === 'playing') {
        setGameState('won');
        setTimeout(() => setShowModal(true), 500);
      }
    } else if (gameMode === 'conversation') {
      const validGuesses = guesses.filter((g) => g !== null);
      const hasWon = correctConversation.participants.every((participant) => {
        return validGuesses.includes(participant);
      });

      if (hasWon && gameState === 'playing') {
        setGameState('won');
        setTimeout(() => setShowModal(true), 500);
      }
    }
  }, [
    guesses,
    correctAnswer,
    correctQuote,
    correctConversation,
    guessLimit,
    gameState,
    gameMode,
  ]);

  const handleGameModeSelect = (mode) => {
    setGameMode(mode);
    setGameState('playing');
    setShowModal(false);
    setCopySuccess(false);
    setInputValue('');
    setInputValue2('');
    setShowAutocomplete(false);
    setShowAutocomplete2(false);
    setSelectedIndex(-1);
    setSelectedIndex2(-1);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const guess = inputValue.trim();

    if (!guess) return;

    // Don't allow new guesses if game is over (except for quote and conversation modes)
    if (gameMode === 'person' && gameState !== 'playing') return;
    if (gameMode === 'quote' && gameState === 'won') return;
    if (gameMode === 'conversation' && gameState === 'won') return;

    if (gameMode === 'conversation') {
      if (guesses.includes(guess)) {
        return; // Prevent duplicate guesses
      }

      // Validate name against people list (check both the proper name and the export key)
      const allPeople = Object.values(people);
      const isValidPerson = allPeople.some(
        (person) => person.name.toLowerCase() === guess.toLowerCase(),
      );
      if (!isValidPerson) {
        return;
      }

      setGuesses((g) => [...g, guess]);
      setInputValue('');
      setShowAutocomplete(false);
      return;
    }
    if (gameState !== 'playing' && gameMode === 'person') return;
    if (gameState === 'won' && gameMode === 'quote') return;

    if (guesses.includes(guess)) {
      return;
    } // Prevent duplicate guesses

    // For person mode, validate against people list
    if (
      gameMode === 'person' &&
      !Object.keys(people).includes(guess.toLowerCase())
    ) {
      return;
    }

    if (gameMode === 'person') {
      setGuesses((g) => [
        guess,
        ...g.slice(0, -1), // take all but the last element
      ]);
    } else if (gameMode === 'quote') {
      setGuesses((g) => [guess, ...g]);
    }

    setInputValue(''); // Clear the input
    setShowAutocomplete(false); // Hide autocomplete
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    const shouldShowAutocomplete =
      value.length > 0 &&
      (gameState === 'playing' ||
        (gameMode === 'quote' && gameState !== 'won') ||
        (gameMode === 'conversation' && gameState !== 'won'));
    setShowAutocomplete(shouldShowAutocomplete);
    setSelectedIndex(-1); // Reset selection when typing
  };

  const handleInputChange2 = (event) => {
    const value = event.target.value;
    setInputValue2(value);
    const shouldShowAutocomplete =
      value.length > 0 && gameMode === 'conversation' && gameState !== 'won';
    setShowAutocomplete2(shouldShowAutocomplete);
    setSelectedIndex2(-1); // Reset selection when typing
  };

  const handleInputFocus = () => {
    const shouldShowAutocomplete =
      gameState === 'playing' ||
      (gameMode === 'quote' && gameState !== 'won') ||
      (gameMode === 'conversation' && gameState !== 'won');
    if (shouldShowAutocomplete) {
      setShowAutocomplete(true);
      setSelectedIndex(-1);
    }
  };

  const handleInputFocus2 = () => {
    const shouldShowAutocomplete =
      gameMode === 'conversation' && gameState !== 'won';
    if (shouldShowAutocomplete) {
      setShowAutocomplete2(true);
      setSelectedIndex2(-1);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding to allow for clicking on suggestions
    setTimeout(() => {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }, 150);
  };

  const handleInputBlur2 = () => {
    // Delay hiding to allow for clicking on suggestions
    setTimeout(() => {
      setShowAutocomplete2(false);
      setSelectedIndex2(-1);
    }, 150);
  };

  const handleAutocompleteSelect = (name) => {
    setInputValue(name);
    setShowAutocomplete(false);
    setSelectedIndex(-1);

    // Auto-submit the guess
    let canSubmit = false;

    if (gameMode === 'person') {
      canSubmit =
        gameState === 'playing' &&
        !guesses.includes(name) &&
        Object.keys(people).includes(name.toLowerCase());
    } else if (gameMode === 'quote') {
      canSubmit = gameState !== 'won' && !guesses.includes(name);
    } else if (gameMode === 'conversation') {
      const allPeople = Object.values(people);
      const isValidPerson = allPeople.some(
        (person) => person.name.toLowerCase() === name.toLowerCase(),
      );
      canSubmit =
        gameState !== 'won' && !guesses.includes(name) && isValidPerson;
    }

    if (canSubmit) {
      if (gameMode === 'person') {
        setGuesses((g) => [
          name,
          ...g.slice(0, -1), // take all but the last element
        ]);
      } else if (gameMode === 'quote') {
        setGuesses((g) => [name, ...g]);
      } else if (gameMode === 'conversation') {
        setGuesses((g) => [name, ...g]);
      }
      setInputValue(''); // Clear the input after submission
    }
  };

  const handleKeyDown = (event) => {
    if (!showAutocomplete) return;

    const allPeople = Object.values(people);
    const filteredPeople = allPeople.filter((person) =>
      person.name.toLowerCase().startsWith(inputValue.toLowerCase()),
    );

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredPeople.length - 1 ? prev + 1 : prev,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      handleAutocompleteSelect(filteredPeople[selectedIndex].name);
    } else if (event.key === 'Escape') {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }
  };

  const handleShare = async () => {
    let correctAnswerForShare;
    if (gameMode === 'person') {
      correctAnswerForShare = correctAnswer;
    } else if (gameMode === 'quote') {
      correctAnswerForShare = correctQuote?.author;
    } else if (gameMode === 'conversation') {
      correctAnswerForShare = correctConversation?.participants;
    }

    const shareText = generateShareText(
      guesses,
      correctAnswerForShare,
      gameState === 'won',
      gameMode,
    );
    const success = await copyToClipboard(shareText);
    setCopySuccess(success);

    // Reset copy success message after 2 seconds
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const resetGame = () => {
    if (gameMode === 'person') {
      setGuesses(Array(guessLimit).fill(null));
    } else {
      setGuesses([]);
    }
    setGameState('playing');
    setShowModal(false);
    setCopySuccess(false);
    setInputValue('');
    setInputValue2('');
    setShowAutocomplete(false);
    setShowAutocomplete2(false);
    setSelectedIndex(-1);
    setSelectedIndex2(-1);
  };

  const changeGameMode = () => {
    setGameMode(null);
    resetGame();
  };

  // Show game mode selector if no mode is selected
  if (!gameMode) {
    return <GameModeSelector onSelectMode={handleGameModeSelect} />;
  }

  const getGuessesRemaining = () => {
    if (gameMode === 'person') {
      return guesses.reduce(function (n, val) {
        return n + (val === null);
      }, 0);
    }
    return '∞'; // Infinite for quote and conversation modes
  };

  return (
    <div className="App px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center justify-center gap-6 sm:gap-8">
        <div className="text-center">
          <p className="mt-2 text-lg font-medium text-white/80">
            {getCurrentDateString()}
          </p>
          <button
            onClick={changeGameMode}
            className="mt-2 text-sm text-white/60 transition-colors hover:text-white/80"
          >
            Change game mode
          </button>
        </div>

        {gameMode === 'quote' && correctQuote && (
          <div className="w-150 rounded-xl border-1 border-white/5 bg-white/5 p-6 backdrop-blur-md">
            <blockquote className="text-center text-lg font-medium text-white italic sm:text-xl">
              {/* {Quote, with "\n" replaced with real <br />} */}"
              {correctQuote.text.split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  {index < correctQuote.text.split('\n').length - 1 && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                </span>
              ))}
              "
            </blockquote>

            {/* Progressive hints based on wrong guesses */}
            <div className="mt-4 space-y-2 text-center text-sm text-white/70">
              <p>
                <span className="font-medium text-white/80">Server:</span>{' '}
                {guesses.length >= 2 ? (
                  correctQuote.server
                ) : (
                  <span className="text-white/50">
                    reveals in {2 - guesses.length} guess
                    {2 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
              <p>
                <span className="font-medium text-white/80">Channel:</span>{' '}
                {guesses.length >= 4 ? (
                  correctQuote.channel
                ) : (
                  <span className="text-white/50">
                    reveals in {4 - guesses.length} guess
                    {4 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
              <p>
                <span className="font-medium text-white/80">Date:</span>{' '}
                {guesses.length >= 6 ? (
                  (correctQuote.date || correctQuote.start) +
                  ' (year-day-month)'
                ) : (
                  <span className="text-white/50">
                    reveals in {6 - guesses.length} guess
                    {6 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {gameMode === 'conversation' && (
          <div className="w-150 rounded-xl border-1 border-white/5 bg-white/5 p-6 backdrop-blur-md">
            <div className="conversation-display overflow-y-auto text-left">
              {correctConversation.messages.map((message, index) => {
                // Check if this message author has been guessed
                const authorGuessed = guesses.includes(message.author);

                // Determine if this is the first message from this author in a sequence
                const isFirstInSequence =
                  index === 0 ||
                  correctConversation.messages[index - 1].author !==
                    message.author;

                // Determine which participant this is based on first appearance in conversation
                const firstMessageIndex =
                  correctConversation.messages.findIndex(
                    (msg) => msg.author === message.author,
                  );
                const participantNumber = firstMessageIndex === 0 ? 1 : 2;
                const participantLabel = `Person ${participantNumber}`;

                // Function to replace participant names in message text
                const replaceParticipantNames = (text) => {
                  let processedText = text;
                  correctConversation.participants.forEach(
                    (participant, index) => {
                      if (!guesses.includes(participant)) {
                        // Replace participant name (case insensitive) with "Person X"
                        const participantNum =
                          correctConversation.messages.findIndex(
                            (msg) => msg.author === participant,
                          ) === 0
                            ? 1
                            : 2;
                        const regex = new RegExp(participant, 'gi');
                        processedText = processedText.replace(
                          regex,
                          `Person ${participantNum}`,
                        );
                      }
                    },
                  );
                  return processedText;
                };

                return (
                  <div
                    key={index}
                    className={isFirstInSequence && index > 0 ? 'mt-6' : ''}
                  >
                    {isFirstInSequence ? (
                      <div className="mb-0.5 flex items-start gap-3">
                        <div className="flex flex-col items-center gap-1">
                          {authorGuessed ? (
                            <img
                              src={`/profile-pics/${message.author.toLowerCase()}.png`}
                              alt={message.author}
                              className="h-11 w-11 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-500">
                              <span className="text-base font-bold text-white">
                                {participantNumber}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="mb-1 text-left text-base leading-tight font-semibold text-gray-400">
                            {authorGuessed ? message.author : participantLabel}
                          </div>
                          <div className="text-left text-base leading-tight font-light text-white">
                            {replaceParticipantNames(message.text)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="h-auto w-11 min-w-[44px]"></div>
                        <div className="text-left text-base leading-tight font-light text-white">
                          {replaceParticipantNames(message.text)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progressive hints based on wrong guesses */}
            <div className="mt-4 space-y-2 text-center text-sm text-white/70">
              <p>
                <span className="font-medium text-white/80">Server:</span>{' '}
                {guesses.length >= 2 ? (
                  correctConversation.server
                ) : (
                  <span className="text-white/50">
                    reveals in {2 - guesses.length} guess
                    {2 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
              <p>
                <span className="font-medium text-white/80">Channel:</span>{' '}
                {guesses.length >= 4 ? (
                  correctConversation.channel
                ) : (
                  <span className="text-white/50">
                    reveals in {4 - guesses.length} guess
                    {4 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
              <p>
                <span className="font-medium text-white/80">Date:</span>{' '}
                {guesses.length >= 6 ? (
                  correctConversation.start.split('T')[0] + ' (year-day-month)'
                ) : (
                  <span className="text-white/50">
                    reveals in {6 - guesses.length} guess
                    {6 - guesses.length === 1 ? '' : 'es'}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="relative w-full max-w-md">
          <div className="relative">
            <input
              ref={inputRef}
              name="guess"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="w-full rounded-xl border-1 border-white/10 bg-white/10 p-3 text-base font-medium text-white placeholder-white/60 backdrop-blur-md transition-all duration-300 focus:border-green-400/50 focus:bg-white/15 focus:ring-2 focus:ring-green-400/30 focus:outline-none sm:p-4 sm:text-lg"
              placeholder={
                gameMode === 'quote' ? 'Who said this quote?' : 'Guess a name…'
              }
              disabled={
                (gameMode === 'person' && gameState !== 'playing') ||
                (gameMode === 'quote' && gameState === 'won')
              }
              autoComplete="off"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck="false"
            />
            <AutocompleteDropdown
              isOpen={showAutocomplete}
              filter={inputValue}
              onSelect={handleAutocompleteSelect}
              inputRef={inputRef}
              selectedIndex={selectedIndex}
            />
          </div>
        </form>
        <div className="rounded-lg bg-white/10 px-4 py-2 backdrop-blur-md">
          <strong className="text-sm font-semibold sm:text-base">
            Guesses left: <span className="">{getGuessesRemaining()}</span>
          </strong>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center gap-6 pt-6 sm:gap-8 sm:pt-8">
        <div className="w-full overflow-x-auto sm:overflow-x-visible">
          <div className="flex min-w-fit flex-col gap-3 sm:gap-5">
            {gameMode === 'person' && guesses[0] && (
              <div className="flex items-center justify-start gap-1 pl-2 sm:justify-center sm:gap-3 sm:pl-0">
                <label className="w-12 flex-shrink-0 text-xs font-semibold sm:w-20 sm:text-sm lg:text-base"></label>
                <label className="w-16 flex-shrink-0 text-center text-xs font-semibold sm:w-30 sm:text-sm lg:text-base">
                  Hair colour
                </label>
                <label className="w-16 flex-shrink-0 text-center text-xs font-semibold sm:w-30 sm:text-sm lg:text-base">
                  Activity level
                </label>
                <label className="w-16 flex-shrink-0 text-center text-xs font-semibold sm:w-30 sm:text-sm lg:text-base">
                  Joined in
                </label>
                <label className="w-16 flex-shrink-0 text-center text-xs font-semibold sm:w-30 sm:text-sm lg:text-base">
                  Invited by
                </label>
                <label className="w-20 flex-shrink-0 text-center text-xs font-semibold sm:w-70 sm:text-sm lg:text-base">
                  Favourite games
                </label>
              </div>
            )}
            {gameMode === 'conversation' && guesses.length > 0 && (
              <div className="flex items-center justify-center gap-3">
                <label className="w-32 flex-shrink-0 text-center text-xs font-semibold sm:w-40 sm:text-sm lg:text-base">
                  Person 1
                </label>
                <label className="w-32 flex-shrink-0 text-center text-xs font-semibold sm:w-40 sm:text-sm lg:text-base">
                  Person 2
                </label>
              </div>
            )}
            {gameMode === 'person' &&
              guesses.map((guess, i) => (
                <GuessBoxes
                  key={i}
                  correctAnswer={correctAnswer}
                  guess={guess}
                  allGuesses={guesses}
                />
              ))}
            {gameMode === 'quote' &&
              correctQuote &&
              guesses.map((guess, i) => (
                <QuoteGuessBoxes
                  key={i}
                  correctAuthor={correctQuote?.author}
                  guess={guess}
                  allGuesses={guesses}
                />
              ))}
            {gameMode === 'conversation' && correctConversation && (
              <div className="space-y-3">
                {/* Show each guess in both columns to check against both participants */}
                {guesses.map((guess, rowIndex) => {
                  // Check if each participant will be guessed in ANY LATER guess (after this row)
                  const participant1WillBeGuessed = guesses
                    .slice(rowIndex + 1)
                    .includes(correctConversation.participants[0]);
                  const participant2WillBeGuessed = guesses
                    .slice(rowIndex + 1)
                    .includes(correctConversation.participants[1]);

                  return (
                    <div
                      key={rowIndex}
                      className="flex items-stretch justify-center gap-3"
                    >
                      {/* Person 1 column */}
                      <div
                        className={`box ${
                          participant1WillBeGuessed
                            ? 'correct'
                            : guess === correctConversation.participants[0]
                              ? 'correct'
                              : 'blank'
                        } flex h-auto w-32 flex-col items-center justify-center px-2 text-sm font-semibold sm:w-40 sm:text-base lg:text-lg`}
                      >
                        {participant1WillBeGuessed
                          ? correctConversation.participants[0]
                          : guess}
                      </div>

                      {/* Person 2 column */}
                      <div
                        className={`box ${
                          participant2WillBeGuessed
                            ? 'correct'
                            : guess === correctConversation.participants[1]
                              ? 'correct'
                              : 'blank'
                        } flex h-auto w-32 flex-col items-center justify-center px-2 text-sm font-semibold sm:w-40 sm:text-base lg:text-lg`}
                      >
                        {participant2WillBeGuessed
                          ? correctConversation.participants[1]
                          : guess}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Win/Loss Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={gameState === 'won' ? 'Nice!' : 'Sucker'}
      >
        <div className="space-y-4 text-center">
          {gameState === 'won' ? (
            <div className="flex flex-col items-center gap-3 sm:gap-4">
              {gameMode === 'person' && (
                <>
                  <img
                    className="h-20 w-20 rounded-xl sm:h-[8rem] sm:w-[8rem]"
                    src={
                      '/profile-pics/' +
                      correctAnswer.name.toLowerCase() +
                      '.png'
                    }
                  />
                  <p className="text-sm font-medium sm:text-base">
                    You got <strong className="">{correctAnswer.name}</strong>{' '}
                    in{' '}
                    <strong className="">
                      {guesses.filter((g) => g !== null).length}
                    </strong>
                    !
                  </p>
                </>
              )}
              {gameMode === 'quote' && (
                <div>
                  <p className="mb-3 text-sm font-medium sm:text-base">
                    Correct! It was{' '}
                    <strong className="">{correctQuote?.author}</strong>
                  </p>
                  <p className="text-xs text-white/70 sm:text-sm">
                    You got it in <strong>{guesses.length}</strong> guess
                    {guesses.length === 1 ? '' : 'es'}!
                  </p>
                </div>
              )}
              {gameMode === 'conversation' && (
                <div className="flex flex-col items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <img
                        className="h-16 w-16 rounded-xl sm:h-20 sm:w-20"
                        src={`/profile-pics/${correctConversation.participants[0].toLowerCase()}.png`}
                        alt={correctConversation.participants[0]}
                      />
                      <span className="text-sm font-medium">
                        {correctConversation.participants[0]}
                      </span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <img
                        className="h-16 w-16 rounded-xl sm:h-20 sm:w-20"
                        src={`/profile-pics/${correctConversation.participants[1].toLowerCase()}.png`}
                        alt={correctConversation.participants[1]}
                      />
                      <span className="text-sm font-medium">
                        {correctConversation.participants[1]}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-white/70 sm:text-sm">
                    You got it in <strong>{guesses.length}</strong> guess
                    {guesses.length === 1 ? '' : 'es'}!
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 sm:gap-4">
              <p className="text-base font-medium text-red-300 sm:text-lg">
                The answer was:
              </p>
              {gameMode === 'conversation' ? (
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center gap-2">
                    <img
                      className="h-20 w-20 rounded-xl sm:h-[8rem] sm:w-[8rem]"
                      src={`/profile-pics/${correctConversation.participants[0].toLowerCase()}.png`}
                      alt={correctConversation.participants[0]}
                    />
                    <span className="text-sm font-bold sm:text-base">
                      {correctConversation.participants[0]}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <img
                      className="h-20 w-20 rounded-xl sm:h-[8rem] sm:w-[8rem]"
                      src={`/profile-pics/${correctConversation.participants[1].toLowerCase()}.png`}
                      alt={correctConversation.participants[1]}
                    />
                    <span className="text-sm font-bold sm:text-base">
                      {correctConversation.participants[1]}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <img
                    className="h-20 w-20 rounded-xl sm:h-[8rem] sm:w-[8rem]"
                    src={
                      '/profile-pics/' +
                      (
                        correctAnswer?.name ?? correctQuote?.author
                      )?.toLowerCase() +
                      '.png'
                    }
                  />
                  <strong className="text-sm font-bold sm:text-base">
                    {correctAnswer?.name ?? correctQuote?.author}
                  </strong>
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={handleShare}
              className="w-full cursor-pointer rounded-xl bg-green-600 px-4 py-3 !text-xl !font-semibold text-white shadow-lg hover:bg-green-600/70 sm:text-base"
            >
              {copySuccess ? 'Copied!' : 'Share'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default App;
